import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { Cookie, Settings, BarChart3, Target, User } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CookiePolicyPage() {
  const lastUpdated = "February 2025";

  // Function to open cookie preferences
  const openCookieSettings = () => {
    localStorage.removeItem('jigsaw_cookie_consent');
    window.location.reload();
  };

  return (
    <MainLayout>
      <SEOHead
        title="Cookie Policy | Jigsaw Events Hub"
        description="Learn about the cookies we use on events.jigsawconferences.co.uk and how to manage your preferences. GDPR compliant cookie policy."
        url="https://events.jigsawconferences.co.uk/cookies"
      />

      <div className="bg-gray-50 min-h-screen py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <Cookie className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
            
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Cookies?</h2>
              <p className="text-gray-700 mb-4">
                Cookies are small text files stored on your device when you visit a website. 
                They help websites remember your preferences, understand how you use the site, 
                and provide a better experience.
              </p>
              <p className="text-gray-700">
                This Cookie Policy explains which cookies we use on <strong>events.jigsawconferences.co.uk</strong>, 
                why we use them, and how you can control them.
              </p>
            </section>

            {/* Manage Preferences */}
            <section className="bg-orange-50 rounded-lg p-6 border border-orange-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-600" />
                Manage Your Cookie Preferences
              </h2>
              <p className="text-gray-700 mb-4">
                You can change your cookie preferences at any time by clicking the button below:
              </p>
              <Button 
                onClick={openCookieSettings}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Settings className="w-4 h-4 mr-2" />
                Open Cookie Settings
              </Button>
            </section>

            {/* Types of Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Types of Cookies We Use</h2>
              
              {/* Necessary */}
              <div className="mb-8 border-l-4 border-green-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  Necessary Cookies (Always Active)
                </h3>
                <p className="text-gray-700 mb-4">
                  These cookies are essential for the website to function. They cannot be disabled.
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-semibold">Cookie Name</th>
                        <th className="text-left py-2 font-semibold">Purpose</th>
                        <th className="text-left py-2 font-semibold">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">jigsaw_cookie_consent</td>
                        <td className="py-2">Stores your cookie consent preference</td>
                        <td className="py-2">1 year</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">jigsaw_cookie_preferences</td>
                        <td className="py-2">Stores your granular cookie choices</td>
                        <td className="py-2">1 year</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-mono text-xs">admin_token</td>
                        <td className="py-2">Authentication for admin users only</td>
                        <td className="py-2">Session</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Analytics */}
              <div className="mb-8 border-l-4 border-blue-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                  Analytics Cookies (Optional)
                </h3>
                <p className="text-gray-700 mb-4">
                  Help us understand how visitors use our website so we can improve it. 
                  Data is anonymized after 2 months.
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-semibold">Cookie Name</th>
                        <th className="text-left py-2 font-semibold">Provider</th>
                        <th className="text-left py-2 font-semibold">Purpose</th>
                        <th className="text-left py-2 font-semibold">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">_ga</td>
                        <td className="py-2">Google Analytics 4</td>
                        <td className="py-2">Distinguishes users</td>
                        <td className="py-2">2 years</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">_ga_*</td>
                        <td className="py-2">Google Analytics 4</td>
                        <td className="py-2">Maintains session state</td>
                        <td className="py-2">2 years</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-mono text-xs">_gid</td>
                        <td className="py-2">Google Analytics 4</td>
                        <td className="py-2">Distinguishes users</td>
                        <td className="py-2">24 hours</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Marketing */}
              <div className="mb-8 border-l-4 border-purple-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-500" />
                  Marketing Cookies (Optional)
                </h3>
                <p className="text-gray-700 mb-4">
                  Allow us to show you relevant advertisements on other platforms based on your 
                  interests. You can opt out at any time.
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-semibold">Cookie Name</th>
                        <th className="text-left py-2 font-semibold">Provider</th>
                        <th className="text-left py-2 font-semibold">Purpose</th>
                        <th className="text-left py-2 font-semibold">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">_fbp</td>
                        <td className="py-2">Meta (Facebook)</td>
                        <td className="py-2">Tracks visits for ad targeting</td>
                        <td className="py-2">3 months</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-mono text-xs">_fbc</td>
                        <td className="py-2">Meta (Facebook)</td>
                        <td className="py-2">Stores click identifier</td>
                        <td className="py-2">3 months</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Personalization */}
              <div className="mb-8 border-l-4 border-teal-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <User className="w-5 h-5 text-teal-500" />
                  Personalization Cookies (Optional)
                </h3>
                <p className="text-gray-700 mb-4">
                  Remember your preferences and show you relevant event recommendations.
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-semibold">Cookie Name</th>
                        <th className="text-left py-2 font-semibold">Purpose</th>
                        <th className="text-left py-2 font-semibold">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">jigsaw_preferences</td>
                        <td className="py-2">Stores your sector preferences</td>
                        <td className="py-2">1 year</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-mono text-xs">jigsaw_recent_events</td>
                        <td className="py-2">Recently viewed events</td>
                        <td className="py-2">30 days</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Browser Settings */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Managing Cookies in Your Browser</h2>
              <p className="text-gray-700 mb-4">
                You can also control cookies through your browser settings. Here's how:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Note: Disabling all cookies may affect website functionality.
              </p>
            </section>

            {/* Third-Party Policies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Cookie Policies</h2>
              <p className="text-gray-700 mb-4">
                For more information about how third parties use cookies:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Analytics Privacy Policy</a></li>
                <li><a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta (Facebook) Privacy Policy</a></li>
              </ul>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions?</h2>
              <p className="text-gray-700">
                If you have questions about our use of cookies, please contact us at{' '}
                <a href="mailto:privacy@jigsawconferences.co.uk" className="text-blue-600 hover:underline">
                  privacy@jigsawconferences.co.uk
                </a>.
              </p>
              <p className="text-gray-700 mt-4">
                For general privacy concerns, see our <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
